import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Calendar,
  CalendarHeart,
  Car,
  Check,
  ClipboardCheck,
  Clock,
  DoorOpen,
  FileBarChart,
  HardHat,
  Heart,
  Inbox,
  LifeBuoy,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  ParkingCircle,
  Receipt,
  ShieldCheck,
  TrendingUp,
  UserCheck,
  Users2,
  Video,
  Vote,
  Wrench,
  X
} from 'lucide-react';
import { Link, type LinkOptions } from '@tanstack/react-router';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { subscribe } from '@/lib/socket';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusPill } from '@/components/ui/status-pill';
import { Button } from '@/components/ui/button';
import {
  NbhFeatureTile,
  NbhGreeting,
  NbhSectionHeader,
  NbhStatTile,
  nbhPalette,
  type NbhPaletteKey
} from '@/components/ui/nbh';
import { formatRelative } from '@/lib/format';
import type { Visitor } from '@hooddesk/shared-types';

interface Complaint {
  id: string;
  title: string;
  body: string;
  category: string;
  status: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
}
interface ComplaintListResponse {
  items: Complaint[];
  next_cursor: string | null;
}

interface CollectionStats {
  window_days: number;
  total_outstanding_inr: number;
  defaulters_count: number;
  avg_days_overdue: number;
  recovered_in_window_inr: number;
  reminders_sent_in_window: number;
  notes_added_in_window: number;
}

interface VisitorListResponse {
  items: Visitor[];
  next_cursor?: string | null;
}

// ─── Community pulse — API response shapes ─────────────────────────
// Server-side: apps/api/src/modules/community-polls/routes.ts list endpoint
// returns the raw prisma row + `options[]` + `_count.votes`. Per-option
// counts are NOT in the list payload (they live on the detail endpoint),
// so we render the per-option bar from `_count.votes` only when the
// option count is encoded — otherwise we fall back to equal-share bars.
interface CommunityPollOption {
  id: string;
  label: string;
  sort_order: number;
}
interface CommunityPoll {
  id: string;
  question: string;
  status: 'draft' | 'active' | 'closed' | 'archived';
  opens_at: string;
  closes_at: string | null;
  options: CommunityPollOption[];
  _count?: { votes?: number };
  // Optional fields that some shaping might surface in future.
  option_counts?: Record<string, number>;
  total_votes?: number;
}
interface CommunityPollListResponse {
  items: CommunityPoll[];
  next_cursor: string | null;
}

interface SocietyEventListItem {
  id: string;
  title: string;
  starts_at: string;
  ends_at?: string | null;
  location?: string | null;
  category?: string;
  status?: string;
  amenity?: { id: string; name: string } | null;
}
interface SocietyEventListResponse {
  items: SocietyEventListItem[];
}

interface CommunityPostAuthor {
  id: string;
  role: 'resident' | 'admin';
  full_name: string;
  unit: string | null;
  block_name: string | null;
}
interface CommunityPost {
  id: string;
  body: string;
  category: string;
  like_count?: number;
  comment_count?: number;
  created_at: string;
  author?: CommunityPostAuthor;
}
interface CommunityPostListResponse {
  items: CommunityPost[];
  next_cursor: string | null;
}

// ─── Safety & operations — API response shapes ─────────────────────
// Guard: apps/api/src/modules/guards/routes.ts → GET /v1/guards returns
// { items: Guard[] } (no next_cursor). The row carries `shift`
// (day/night/rotating), `active`, `full_name`, `duty_kind`, plus an
// optional `gate` / `tower` nested object. There is NO `on_duty` /
// `is_on_shift` boolean today, so we fall back to "first N active guards".
interface GuardListItem {
  id: string;
  full_name: string;
  shift?: 'day' | 'night' | 'rotating' | null;
  duty_kind?: 'gate' | 'tower' | 'patrol' | 'common_area' | null;
  active?: boolean;
  on_duty?: boolean;
  is_on_shift?: boolean;
  gate?: { id: string; name: string } | null;
  tower?: { id: string; name: string } | null;
}
interface GuardListResponse {
  items: GuardListItem[];
  next_cursor?: string | null;
}

// Camera: apps/api/src/modules/cameras/routes.ts → list returns
// { items, next_cursor }. We treat `status === 'active'` as "online";
// `is_online` is not on the row, so we synthesise from `status`.
interface CameraListItem {
  id: string;
  name: string;
  status?: 'active' | 'offline' | 'maintenance' | 'decommissioned';
  placement?: string;
  location_label?: string | null;
  is_online?: boolean;
  last_ping_ok?: boolean | null;
  gate_id?: string | null;
}
interface CameraListResponse {
  items: CameraListItem[];
  next_cursor?: string | null;
}

// Gate: apps/api/src/modules/gates/routes.ts → GET /v1/society/gates
// returns { items: Gate[] } with id, name, is_primary, active, plus a
// `_count.guards`. Visitor rows do NOT carry a `gate_id` today, so the
// gate-wise card degrades gracefully: it shows a single "all entries"
// bar when there's only one gate, and otherwise an empty-state hint.
interface GateListItem {
  id: string;
  name: string;
  is_primary?: boolean;
  active?: boolean;
  _count?: { guards?: number };
}
interface GateListResponse {
  items: GateListItem[];
}

function todayBounds(): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).toISOString();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
  return { from: start, to: end };
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// Deterministic palette pick so the same visitor always gets the same
// avatar colour (and no two adjacent rows hit the same one for typical
// data, since the 8-bucket hash spreads names well).
const AVATAR_PALETTES: ReadonlyArray<NbhPaletteKey> = [
  'sky',
  'mint',
  'lavender',
  'peach',
  'salmon',
  'teal',
  'yellow',
  'blush'
];

function avatarPalette(name: string): NbhPaletteKey {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTES[hash % AVATAR_PALETTES.length];
}

// INR formatter — compact for tile real-estate (e.g. ₹1.2L, ₹52k).
function compactInr(n: number): string {
  if (!Number.isFinite(n)) return '₹0';
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}k`;
  return `₹${n.toLocaleString('en-IN')}`;
}

const PRIORITY_PALETTE: Record<Complaint['priority'], NbhPaletteKey> = {
  urgent: 'salmon',
  high: 'peach',
  normal: 'sky',
  low: 'mint'
};

const CATEGORY_LABEL: Record<string, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  housekeeping: 'Housekeeping',
  security: 'Security',
  garden: 'Garden',
  lift: 'Lift',
  parking: 'Parking',
  pest_control: 'Pest control',
  amenity: 'Amenity',
  other: 'Other'
};

export function DashboardPage(): JSX.Element {
  const { from, to } = todayBounds();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const todayQuery = useQuery({
    queryKey: ['visitors', 'today', from, to],
    queryFn: () => get<VisitorListResponse>('/visitors', { params: { from, to, limit: 50 } })
  });

  const pendingQuery = useQuery({
    queryKey: ['visitors', 'pending'],
    queryFn: () => get<VisitorListResponse>('/visitors', { params: { status: 'pending', limit: 20 } })
  });

  // Open helpdesk tickets — admin sees society-wide list when `mine` is omitted.
  const helpdeskQuery = useQuery({
    queryKey: ['complaints', 'open', 'dashboard'],
    queryFn: () =>
      get<ComplaintListResponse>('/complaints', {
        params: { status: 'open', sort: 'priority_desc', limit: 5 }
      })
  });

  // Collections snapshot — last 30 days. Optional endpoint; 403 / 404 is OK
  // (finance committee roles only). Errors fall through to a graceful card.
  const collectionsQuery = useQuery({
    queryKey: ['collections', 'stats', 30],
    queryFn: () => get<CollectionStats>('/collections/stats', { params: { days: 30 } }),
    retry: false
  });

  // Community pulse — three non-critical sections. All gracefully degrade
  // if the endpoint is missing or returns an unexpected shape.
  const pollsQuery = useQuery({
    queryKey: ['community-polls', 'open', 'dashboard'],
    queryFn: () => get<CommunityPollListResponse>('/community-polls', { params: { status: 'active', limit: 10 } }),
    retry: false
  });

  const eventsQuery = useQuery({
    queryKey: ['society-events', 'upcoming'],
    queryFn: () => get<SocietyEventListResponse>('/society-events/upcoming', { params: { limit: 10 } }),
    retry: false
  });

  const postsQuery = useQuery({
    queryKey: ['community-posts', 'dashboard'],
    queryFn: () => get<CommunityPostListResponse>('/community/posts', { params: { limit: 5 } }),
    retry: false
  });

  // Safety & operations — guards roster, CCTV tiles, gates breakdown.
  // All three degrade silently on 4xx/5xx: the row simply renders its
  // empty / error state without blocking the rest of the dashboard.
  const guardsQuery = useQuery({
    queryKey: ['guards', 'dashboard'],
    queryFn: () => get<GuardListResponse>('/guards'),
    retry: false
  });

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'dashboard'],
    queryFn: () => get<CameraListResponse>('/cameras', { params: { limit: 8 } }),
    retry: false
  });

  // Gate names are looked up so we can label any future per-gate breakdown.
  // Today the Visitor row carries no gate_id, so we use this list mostly
  // to drive the empty / single-gate fallback path.
  const gatesQuery = useQuery({
    queryKey: ['gates', 'dashboard'],
    queryFn: () => get<GateListResponse>('/society/gates'),
    retry: false
  });

  // Inline approve/deny — used by the pending-approvals queue. On success
  // invalidate both today + pending visitor lists so KPIs + table refresh.
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'deny' }) =>
      post(`/visitors/${id}/approve`, { decision }),
    onSuccess: (_data, vars) => {
      toast.success(vars.decision === 'approve' ? 'Visitor approved' : 'Visitor denied');
      void queryClient.invalidateQueries({ queryKey: ['visitors', 'pending'] });
      void queryClient.invalidateQueries({ queryKey: ['visitors', 'today'] });
    },
    onError: (err) => {
      toast.error('Could not save decision', { description: getErrorMessage(err) });
    }
  });

  const todayCount = todayQuery.data?.items.length ?? 0;
  const pendingCount = pendingQuery.data?.items.length ?? 0;
  const checkedIn = todayQuery.data?.items.filter((v) => v.status === 'checked_in').length ?? 0;
  const pendingItems = (pendingQuery.data?.items ?? []).slice(0, 5);
  const helpdeskItems = (helpdeskQuery.data?.items ?? []).slice(0, 3);
  const helpdeskCount = helpdeskQuery.data?.items.length ?? 0;
  const collections = collectionsQuery.data;

  const recent = (todayQuery.data?.items ?? []).slice(0, 10);
  const isInitial = todayQuery.isLoading || pendingQuery.isLoading;

  // Realtime — surface panic alerts as a red toast. Whether this client receives
  // the event at all is controlled server-side by Society.sos_broadcast_scope
  // (admins are always in the recipient set).
  useEffect(() => {
    const off = subscribe('panic:raised', (e) => {
      if (e.type !== 'panic:raised') return;
      const kind = (e.panic_kind ?? 'security').toUpperCase();
      toast.error(`Panic alert: ${kind}`, {
        description: `Raised at ${new Date(e.ts).toLocaleTimeString()}. Coordinate response now.`,
        duration: 15_000
      });
    });
    return () => {
      off();
    };
  }, []);

  const adminName = user?.full_name?.split(' ')[0] ?? 'Admin';
  const greetingLead = `${timeOfDayGreeting()}, ${adminName}`;
  const greetingSub = isInitial
    ? 'Loading today’s activity…'
    : `${todayCount} visitor${todayCount === 1 ? '' : 's'} today · ${pendingCount} pending`;

  // Semantic palette mapping:
  //   sky      → information / activity logs / tracking
  //   mint     → approved / safe / wellness
  //   yellow   → announcements / pending attention
  //   lavender → people / community
  //   peach    → finance / money / billing
  //   salmon   → urgent / SOS / alerts
  //   teal     → transport / mobility
  //   blush    → recreation / social / amenities
  //
  // Awaiting-approval tile escalates yellow → salmon (red) when the
  // backlog crosses the "needs action now" threshold.
  const pendingPalette: NbhPaletteKey = pendingCount > 5 ? 'salmon' : 'yellow';

  // 12-tile feature grid. Colour follows feature meaning, not aesthetics:
  // money tiles share peach, people share lavender, transport teal, etc.
  const features: ReadonlyArray<{
    label: string;
    icon: typeof Activity;
    palette: NbhPaletteKey;
    to: LinkOptions['to'];
  }> = [
    { label: 'Visitor logs', icon: Activity, palette: 'sky', to: '/visitor-logs' },
    { label: 'Pre-approve', icon: UserCheck, palette: 'mint', to: '/frequent-visitors' },
    { label: 'Notices', icon: Megaphone, palette: 'yellow', to: '/notices' },
    { label: 'Residents', icon: Users2, palette: 'lavender', to: '/residents' },
    { label: 'Bills', icon: Receipt, palette: 'peach', to: '/bills' },
    { label: 'Helpdesk', icon: LifeBuoy, palette: 'salmon', to: '/helpdesk' },
    { label: 'Parking', icon: ParkingCircle, palette: 'teal', to: '/parking' },
    { label: 'Amenities', icon: Calendar, palette: 'blush', to: '/amenities' },
    // Vendors share peach with Bills — both are "commerce / pay external" tiles.
    { label: 'Vendors', icon: Wrench, palette: 'peach', to: '/vendors' },
    // Staff share mint with Pre-approve — both are "our trusted people" tiles.
    { label: 'Staff', icon: HardHat, palette: 'mint', to: '/staff' },
    // Events share lavender with Residents — both are "community" tiles.
    { label: 'Events', icon: CalendarHeart, palette: 'lavender', to: '/society-events' },
    // Attendance share sky with Visitor logs — both are "tracking" tiles.
    { label: 'Attendance', icon: ClipboardCheck, palette: 'sky', to: '/attendance' }
  ];

  const adminTools: ReadonlyArray<{
    label: string;
    icon: typeof Activity;
    palette: NbhPaletteKey;
    to: LinkOptions['to'];
  }> = [
    // SOS incidents is the loudest tile on the page — salmon (red) on purpose.
    { label: 'SOS incidents', icon: AlertTriangle, palette: 'salmon', to: '/panic-incidents' },
    // Audit log is "scrutiny" — yellow caution colour.
    { label: 'Audit log', icon: ClipboardCheck, palette: 'yellow', to: '/audit-log' },
    // Compliance is "safe / verified" — mint.
    { label: 'Compliance', icon: ShieldCheck, palette: 'mint', to: '/compliance' },
    // Reports is "data / analytics" — sky.
    { label: 'Reports', icon: FileBarChart, palette: 'sky', to: '/reports' }
  ];

  return (
    <>
      <PageHeader title="Dashboard" />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-6 px-vt-6 pb-vt-8 pt-vt-4">
        <NbhGreeting greeting={greetingLead} status={greetingSub} />

        <div className="grid grid-cols-1 gap-vt-6 md:grid-cols-3">
          {/* Today's entries — sky (tracking / live activity) */}
          <NbhStatTile
            label="Today's entries"
            value={isInitial ? <Skeleton className="h-9 w-20" /> : todayCount}
            icon={Activity}
            palette="sky"
          />
          {/* Awaiting approval — yellow (waiting) escalates to salmon (red, urgent) */}
          <NbhStatTile
            label="Awaiting approval"
            value={isInitial ? <Skeleton className="h-9 w-20" /> : pendingCount}
            icon={AlertTriangle}
            palette={pendingPalette}
            sub={pendingCount > 5 ? 'Backlog growing — review now' : undefined}
          />
          {/* Currently inside — mint (residents safely in-house) */}
          <NbhStatTile
            label="Currently inside"
            value={isInitial ? <Skeleton className="h-9 w-20" /> : checkedIn}
            icon={Users2}
            palette="mint"
          />
        </div>

        <section className="space-y-vt-3">
          <NbhSectionHeader
            title="Quick actions"
            subtitle="Jump into common society operations."
            palette="lavender"
          />
          <div className="grid grid-cols-3 gap-vt-3 sm:grid-cols-4 lg:grid-cols-6">
            {features.map((f) => (
              <NbhFeatureTile
                key={f.label}
                label={f.label}
                icon={f.icon}
                palette={f.palette}
                to={f.to}
                badgeCount={f.to === '/visitor-logs' && pendingCount > 0 ? pendingCount : undefined}
              />
            ))}
          </div>
        </section>

        <section className="space-y-vt-3">
          <NbhSectionHeader
            title="Admin tools"
            subtitle="Governance, compliance and incident review."
            palette="teal"
          />
          <div className="grid grid-cols-2 gap-vt-3 sm:grid-cols-4">
            {adminTools.map((f) => (
              <NbhFeatureTile
                key={f.label}
                label={f.label}
                icon={f.icon}
                palette={f.palette}
                to={f.to}
              />
            ))}
          </div>
        </section>

        {/* Two-column action row: Pending approvals + Open helpdesk tickets. */}
        <div className="grid grid-cols-1 gap-vt-6 lg:grid-cols-2">
          <PendingApprovalsCard
            items={pendingItems}
            count={pendingCount}
            loading={pendingQuery.isLoading}
            onDecide={(id, decision) => decide.mutate({ id, decision })}
            decidingId={decide.isPending ? decide.variables?.id ?? null : null}
          />
          <HelpdeskTicketsCard
            items={helpdeskItems}
            count={helpdeskCount}
            loading={helpdeskQuery.isLoading}
            error={helpdeskQuery.isError}
          />
        </div>

        <CollectionsThisMonthCard
          stats={collections}
          loading={collectionsQuery.isLoading}
          error={collectionsQuery.isError}
        />

        {/* Community pulse — polls + upcoming events side-by-side on lg, posts full-width below. */}
        <div className="grid grid-cols-1 gap-vt-6 lg:grid-cols-2">
          <ActivePollsCard
            items={pollsQuery.data?.items ?? []}
            loading={pollsQuery.isLoading}
            error={pollsQuery.isError}
          />
          <UpcomingEventsCard
            items={eventsQuery.data?.items ?? []}
            loading={eventsQuery.isLoading}
            error={eventsQuery.isError}
          />
        </div>

        <RecentCommunityPostsCard
          items={postsQuery.data?.items ?? []}
          loading={postsQuery.isLoading}
          error={postsQuery.isError}
        />

        {/* Safety & operations — guards roster + live CCTV tiles. */}
        <div className="grid grid-cols-1 gap-vt-6 lg:grid-cols-2">
          <GuardsOnDutyCard
            items={guardsQuery.data?.items ?? []}
            loading={guardsQuery.isLoading}
            error={guardsQuery.isError}
          />
          <CctvMiniGridCard
            items={camerasQuery.data?.items ?? []}
            loading={camerasQuery.isLoading}
            error={camerasQuery.isError}
          />
        </div>

        {/* Safety & operations — today's traffic split + gate-wise entries. */}
        <div className="grid grid-cols-1 gap-vt-6 lg:grid-cols-2">
          <VehicleTrafficSplitCard
            visitors={todayQuery.data?.items ?? []}
            loading={todayQuery.isLoading}
          />
          <GateWiseEntriesCard
            visitors={todayQuery.data?.items ?? []}
            gates={gatesQuery.data?.items ?? []}
            loading={todayQuery.isLoading || gatesQuery.isLoading}
          />
        </div>

        <section className="space-y-vt-3">
          <NbhSectionHeader
            title="Recent entries"
            subtitle="Last 10 visitor events for today."
            palette="sky"
            rightAction={
              <Button asChild variant="ghost" size="sm">
                <Link to="/visitor-logs">
                  View all <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
            }
          />
          <Card className="rounded-vt-lg">
            <CardContent className="pt-vt-5">
              {todayQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-11 w-full" />
                  ))}
                </div>
              ) : todayQuery.isError ? (
                <EmptyState
                  icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
                  title="Could not load today's entries"
                  description={(todayQuery.error as Error)?.message ?? 'Check API connectivity and try again.'}
                  action={
                    <Button variant="secondary" onClick={() => void todayQuery.refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : recent.length === 0 ? (
                <EmptyState
                  icon={<Inbox className="h-6 w-6" strokeWidth={1.75} />}
                  title="No visitors yet today"
                  description="Entries will appear here in real time as guards check guests in."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitor</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((v) => {
                      const avatar = nbhPalette(avatarPalette(v.visitor_name));
                      return (
                        <TableRow key={v.id}>
                          <TableCell>
                            <div className="flex items-center gap-vt-3">
                              <span
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-vt-12 font-bold text-white shadow-vt-1 ring-2 ring-white/60"
                                style={{ backgroundColor: avatar.fg }}
                              >
                                {v.visitor_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                              </span>
                              <div className="min-w-0">
                                <div className="truncate font-medium text-vt-text">{v.visitor_name}</div>
                                <div className="truncate text-vt-12 text-vt-text-muted">{v.phone}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="capitalize text-vt-text-muted">{v.visit_kind}</span>
                          </TableCell>
                          <TableCell>
                            <StatusPill status={v.status} />
                          </TableCell>
                          <TableCell className="text-right text-vt-text-muted">
                            {formatRelative(v.checked_in_at ?? v.expected_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </section>

        <section className="space-y-vt-3">
          <NbhSectionHeader
            title="Activity at a glance"
            subtitle="Hourly entries / exits across today."
            palette="sky"
          />
          <Card className="rounded-vt-lg">
            <CardContent className="pt-vt-5">
              <HourlyActivityChart visitors={todayQuery.data?.items ?? []} loading={todayQuery.isLoading} />
            </CardContent>
          </Card>
        </section>
      </div>
    </>
  );
}

interface HourlyActivityChartProps {
  visitors: Visitor[];
  loading: boolean;
}

// Computes 24 hourly entry counts from today's visitor list and renders a
// pastel-bar histogram. Uses the same NBH palette as the rest of the page —
// sky tint baseline + lavender peaks, with the busiest hour called out.
function HourlyActivityChart({ visitors, loading }: HourlyActivityChartProps): JSX.Element {
  const sky = nbhPalette('sky');
  const lavender = nbhPalette('lavender');

  if (loading) {
    return (
      <div className="flex h-40 items-end gap-1">
        {Array.from({ length: 24 }).map((_, i) => (
          <Skeleton key={i} className="flex-1 rounded-vt-sm" style={{ height: `${30 + ((i * 7) % 50)}%` }} />
        ))}
      </div>
    );
  }

  const buckets: number[] = Array(24).fill(0);
  for (const v of visitors) {
    const iso = v.checked_in_at ?? v.expected_at;
    if (!iso) continue;
    const h = new Date(iso).getHours();
    if (h >= 0 && h < 24) buckets[h] += 1;
  }
  const max = Math.max(1, ...buckets);
  const peakHour = buckets.indexOf(max);
  const total = buckets.reduce((a, b) => a + b, 0);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center gap-vt-3 py-vt-8">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-vt-2 ring-4 ring-white/60"
          style={{ backgroundColor: sky.fg }}
        >
          <Activity className="h-6 w-6" strokeWidth={2.5} />
        </span>
        <div className="text-center">
          <div className="text-vt-14 font-semibold text-vt-text">Quiet so far today</div>
          <div className="mt-0.5 text-vt-12 text-vt-text-muted">
            Hourly entries will appear here as guards check guests in.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-vt-3">
      <div className="flex items-end gap-1" style={{ height: 160 }}>
        {buckets.map((count, h) => {
          const isPeak = h === peakHour;
          const heightPct = (count / max) * 100;
          return (
            <div
              key={h}
              className="group relative flex-1 rounded-vt-sm transition-opacity duration-vt hover:opacity-80"
              style={{
                height: `${Math.max(4, heightPct)}%`,
                background: `linear-gradient(180deg, ${isPeak ? lavender.bgFrom : sky.bgFrom}, ${isPeak ? lavender.fg : sky.fg})`
              }}
              title={`${h.toString().padStart(2, '0')}:00 — ${count} entr${count === 1 ? 'y' : 'ies'}`}
            >
              {/* Hour label on every 6th bar */}
              {h % 6 === 0 ? (
                <span className="pointer-events-none absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-medium text-vt-text-soft">
                  {h.toString().padStart(2, '0')}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-vt-3 pt-vt-3 text-vt-12 text-vt-text-muted">
        <span>
          Peak at <span className="font-semibold text-vt-text">{peakHour.toString().padStart(2, '0')}:00</span>
          {' · '}
          <span className="font-semibold text-vt-text">{max}</span> entries
        </span>
        <span>
          Total today: <span className="font-semibold text-vt-text">{total}</span>
        </span>
      </div>
    </div>
  );
}

// ─── PendingApprovalsCard ─────────────────────────────────────────
// Top-5 pending visitors with inline Approve / Deny mutations.
// Header palette escalates to salmon when backlog crosses the
// "needs action now" threshold (>5).

interface PendingApprovalsCardProps {
  items: Visitor[];
  count: number;
  loading: boolean;
  onDecide: (id: string, decision: 'approve' | 'deny') => void;
  decidingId: string | null;
}

function PendingApprovalsCard({
  items,
  count,
  loading,
  onDecide,
  decidingId
}: PendingApprovalsCardProps): JSX.Element {
  const palette: NbhPaletteKey = count > 5 ? 'salmon' : 'yellow';
  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Pending approvals"
        subtitle={count > 0 ? `${count} visitor${count === 1 ? '' : 's'} awaiting decision` : 'Inbox zero — nice'}
        palette={palette}
        rightAction={
          count > 5 ? (
            <Button asChild variant="ghost" size="sm">
              <Link to="/visitor-logs">
                Review all <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Button>
          ) : undefined
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="space-y-vt-2 pt-vt-5">
          {loading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Check className="h-6 w-6" strokeWidth={1.75} />}
              title="No pending approvals"
              description="Walk-in visitors will appear here as guards raise them."
            />
          ) : (
            items.map((v) => {
              const avatar = nbhPalette(avatarPalette(v.visitor_name));
              const busy = decidingId === v.id;
              return (
                <div
                  key={v.id}
                  className="flex items-center gap-vt-3 rounded-vt-md border border-vt-border-soft bg-vt-surface p-vt-3 transition-colors duration-vt hover:bg-vt-surface-2"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-vt-13 font-bold text-white shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: avatar.fg }}
                  >
                    {v.visitor_name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-14 font-semibold text-vt-text">{v.visitor_name}</div>
                    <div className="mt-0.5 flex items-center gap-vt-2 text-vt-12 text-vt-text-muted">
                      <span className="capitalize">{v.visit_kind}</span>
                      <span aria-hidden="true">·</span>
                      <Clock className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
                      <span>{formatRelative(v.expected_at)}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-vt-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDecide(v.id, 'deny')}
                      disabled={busy}
                      aria-label={`Deny ${v.visitor_name}`}
                      title="Deny"
                      className="text-vt-alert hover:bg-vt-alert/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onDecide(v.id, 'approve')}
                      disabled={busy}
                      loading={busy}
                      loadingText="…"
                      aria-label={`Approve ${v.visitor_name}`}
                      title="Approve"
                    >
                      <Check className="h-4 w-4" /> Approve
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── HelpdeskTicketsCard ──────────────────────────────────────────
// Top 3 open complaints sorted by priority desc. Each row links to
// the helpdesk page for triage. Palette salmon since open tickets
// always represent unresolved resident pain.

interface HelpdeskTicketsCardProps {
  items: Complaint[];
  count: number;
  loading: boolean;
  error: boolean;
}

function HelpdeskTicketsCard({ items, count, loading, error }: HelpdeskTicketsCardProps): JSX.Element {
  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Open helpdesk tickets"
        subtitle={loading ? 'Loading…' : count > 0 ? `${count} open · top ${Math.min(items.length, 3)} shown` : 'No open tickets'}
        palette="salmon"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/helpdesk">
              Open helpdesk <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="space-y-vt-2 pt-vt-5">
          {loading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load tickets"
              description="The helpdesk feed is unavailable right now."
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<LifeBuoy className="h-6 w-6" strokeWidth={1.75} />}
              title="No open tickets"
              description="When residents raise a helpdesk request, it'll surface here."
            />
          ) : (
            items.map((c) => {
              const p = nbhPalette(PRIORITY_PALETTE[c.priority]);
              return (
                <Link
                  key={c.id}
                  to="/helpdesk"
                  className="flex items-center gap-vt-3 rounded-vt-md border border-vt-border-soft bg-vt-surface p-vt-3 transition-colors duration-vt hover:bg-vt-surface-2"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: p.fg }}
                  >
                    <LifeBuoy className="h-5 w-5" strokeWidth={2.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-14 font-semibold text-vt-text">{c.title}</div>
                    <div className="mt-0.5 flex items-center gap-vt-2 text-vt-12 text-vt-text-muted">
                      <span>{CATEGORY_LABEL[c.category] ?? c.category}</span>
                      <span aria-hidden="true">·</span>
                      <Clock className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
                      <span>{formatRelative(c.created_at)}</span>
                    </div>
                  </div>
                  <span
                    className="shrink-0 rounded-vt-pill px-vt-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white"
                    style={{ backgroundColor: p.fg }}
                  >
                    {c.priority}
                  </span>
                </Link>
              );
            })
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── CollectionsThisMonthCard ─────────────────────────────────────
// 4 mini-tiles: recovered / outstanding / defaulters / avg overdue.
// Recovered = peach (money in). Outstanding escalates to salmon
// when defaulters cross threshold. Avg-overdue = yellow (waiting).

interface CollectionsThisMonthCardProps {
  stats: CollectionStats | undefined;
  loading: boolean;
  error: boolean;
}

function CollectionsThisMonthCard({ stats, loading, error }: CollectionsThisMonthCardProps): JSX.Element {
  const outstandingPalette: NbhPaletteKey = (stats?.defaulters_count ?? 0) > 5 ? 'salmon' : 'peach';
  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Collections this month"
        subtitle="Last 30 days · dues, recovery, defaulters."
        palette="peach"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/collections">
              Open collections <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      {error ? (
        <Card className="rounded-vt-lg">
          <CardContent className="pt-vt-5">
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Collections snapshot unavailable"
              description="Your role may not have access, or the endpoint is offline."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-vt-4 md:grid-cols-4">
          <NbhStatTile
            label="Recovered (30d)"
            value={loading || !stats ? <Skeleton className="h-9 w-24" /> : compactInr(stats.recovered_in_window_inr)}
            icon={TrendingUp}
            palette="mint"
          />
          <NbhStatTile
            label="Outstanding"
            value={loading || !stats ? <Skeleton className="h-9 w-24" /> : compactInr(stats.total_outstanding_inr)}
            icon={Receipt}
            palette={outstandingPalette}
            sub={stats && stats.defaulters_count > 5 ? 'High default rate' : undefined}
          />
          <NbhStatTile
            label="Defaulters"
            value={loading || !stats ? <Skeleton className="h-9 w-20" /> : stats.defaulters_count}
            icon={Users2}
            palette={outstandingPalette}
          />
          <NbhStatTile
            label="Avg days overdue"
            value={loading || !stats ? <Skeleton className="h-9 w-20" /> : `${stats.avg_days_overdue}d`}
            icon={Clock}
            palette="yellow"
          />
        </div>
      )}
    </section>
  );
}

// ─── ActivePollsCard ──────────────────────────────────────────────
// Top 3 active polls. Each row shows the question, total vote count
// and a horizontal share-bar per option. Lavender palette throughout
// (community / people semantic). The list endpoint doesn't return
// per-option counts — only an aggregate `_count.votes` — so when
// option_counts is absent we render zero-progress bars and just
// surface the total vote count.

interface ActivePollsCardProps {
  items: CommunityPoll[];
  loading: boolean;
  error: boolean;
}

function ActivePollsCard({ items, loading, error }: ActivePollsCardProps): JSX.Element {
  const lavender = nbhPalette('lavender');
  // Filter to active / unexpired polls client-side. The API already filters
  // by status when we send ?status=active, but be defensive.
  const now = Date.now();
  const open = items.filter((p) => {
    if (p.status && p.status !== 'active') return false;
    if (p.closes_at && new Date(p.closes_at).getTime() < now) return false;
    return true;
  });
  const top3 = open.slice(0, 3);

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Active polls"
        subtitle={
          loading
            ? 'Loading…'
            : open.length > 0
              ? `${open.length} open · top ${top3.length} shown`
              : 'No polls open right now'
        }
        palette="lavender"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/community-polls">
              Open polls <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="space-y-vt-3 pt-vt-5">
          {loading ? (
            <div className="flex flex-col gap-vt-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load polls"
              description="The community polls feed is unavailable right now."
            />
          ) : top3.length === 0 ? (
            <EmptyState
              icon={<Vote className="h-6 w-6" strokeWidth={1.75} />}
              title="No polls open right now"
              description="When the committee opens a sentiment poll, it'll surface here."
            />
          ) : (
            top3.map((poll) => {
              const total = poll.total_votes ?? poll._count?.votes ?? 0;
              const counts = poll.option_counts ?? {};
              // Determine the leading option for the vivid lavender bar.
              let leadId: string | null = null;
              let leadCount = -1;
              for (const o of poll.options) {
                const c = counts[o.id] ?? 0;
                if (c > leadCount) {
                  leadCount = c;
                  leadId = o.id;
                }
              }
              return (
                <div
                  key={poll.id}
                  className="space-y-vt-2 rounded-vt-md border border-vt-border-soft bg-vt-surface p-vt-3"
                >
                  <div className="flex items-start gap-vt-3">
                    <span
                      className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow-vt-1 ring-2 ring-white/60"
                      style={{ backgroundColor: lavender.fg }}
                    >
                      <Vote className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-14 font-semibold text-vt-text">{poll.question}</div>
                      <div className="mt-0.5 text-vt-12 text-vt-text-muted">
                        {total} vote{total === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-vt-2">
                    {poll.options.slice(0, 4).map((opt) => {
                      const c = counts[opt.id] ?? 0;
                      const pct = total > 0 ? Math.round((c / total) * 100) : 0;
                      const isLead = opt.id === leadId && leadCount > 0;
                      return (
                        <div key={opt.id}>
                          <div className="mb-0.5 flex items-center justify-between gap-vt-2 text-vt-12">
                            <span className="truncate font-medium text-vt-text">{opt.label}</span>
                            <span className="shrink-0 text-vt-text-muted">
                              {total > 0 ? `${pct}%` : '—'}
                            </span>
                          </div>
                          <div
                            className="h-2 w-full overflow-hidden rounded-full"
                            style={{ backgroundColor: lavender.bgFrom }}
                          >
                            <div
                              className="h-full rounded-full transition-all duration-vt"
                              style={{
                                width: `${total > 0 ? Math.max(2, pct) : 0}%`,
                                backgroundColor: isLead ? lavender.fg : lavender.bgTo
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── UpcomingEventsCard ───────────────────────────────────────────
// Horizontal scrollable carousel of up-to-5 events in the next 7 days.
// Blush palette (recreation / social). Falls back gracefully if the
// endpoint returns events further out — we just filter client-side.

interface UpcomingEventsCardProps {
  items: SocietyEventListItem[];
  loading: boolean;
  error: boolean;
}

function formatEventDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${day} · ${time}`;
}

function UpcomingEventsCard({ items, loading, error }: UpcomingEventsCardProps): JSX.Element {
  const blush = nbhPalette('blush');
  const now = Date.now();
  const sevenDaysOut = now + 7 * 24 * 60 * 60 * 1000;
  const upcoming = items
    .filter((e) => {
      const t = new Date(e.starts_at).getTime();
      return !Number.isNaN(t) && t >= now && t <= sevenDaysOut;
    })
    .slice(0, 5);

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Upcoming events"
        subtitle="Next 7 days."
        palette="blush"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/society-events">
              Open events <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="pt-vt-5">
          {loading ? (
            <div className="flex gap-vt-3 overflow-hidden">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-36 w-60 shrink-0 rounded-vt-lg" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load events"
              description="The events feed is unavailable right now."
            />
          ) : upcoming.length === 0 ? (
            <EmptyState
              icon={<CalendarHeart className="h-6 w-6" strokeWidth={1.75} />}
              title="No events in the next 7 days"
              description="Newly-published events will surface here as the committee schedules them."
            />
          ) : (
            <div className="flex gap-vt-3 overflow-x-auto pb-vt-2">
              {upcoming.map((ev) => {
                const venue = ev.location ?? ev.amenity?.name ?? null;
                return (
                  <Link
                    key={ev.id}
                    to="/society-events"
                    className="group relative flex w-60 shrink-0 flex-col gap-vt-3 overflow-hidden rounded-vt-lg p-vt-4 shadow-vt-1 transition-all duration-vt ease-vt hover:-translate-y-px hover:shadow-vt-2"
                    style={{ background: `linear-gradient(135deg, ${blush.bgFrom} 0%, ${blush.bgTo} 100%)` }}
                  >
                    <span aria-hidden="true" className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-white/45" />
                    <span aria-hidden="true" className="absolute -right-2 -top-2 h-8 w-8 rounded-full bg-white/30" />
                    <span
                      className="relative flex h-11 w-11 items-center justify-center rounded-full shadow-vt-2 ring-4 ring-white/60"
                      style={{ backgroundColor: blush.fg }}
                    >
                      <Calendar className="h-5 w-5 text-white" strokeWidth={2.5} />
                    </span>
                    <div className="relative min-w-0">
                      <div
                        className="line-clamp-2 text-vt-14 font-bold leading-tight"
                        style={{ color: blush.fgDeep }}
                      >
                        {ev.title}
                      </div>
                      <div className="mt-vt-2 text-vt-12 font-semibold" style={{ color: blush.fg }}>
                        {formatEventDate(ev.starts_at)}
                      </div>
                      {venue ? (
                        <div className="mt-0.5 truncate text-vt-12" style={{ color: blush.fgDeep }}>
                          {venue}
                        </div>
                      ) : null}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── RecentCommunityPostsCard ─────────────────────────────────────
// Last 5 posts from the resident wall. Avatar disc uses the deterministic
// NBH palette helper. Reactions summary (likes + comments) renders when
// the API exposes the denormalised counters; otherwise we just show the
// relative timestamp.

interface RecentCommunityPostsCardProps {
  items: CommunityPost[];
  loading: boolean;
  error: boolean;
}

function excerpt(s: string, max = 80): string {
  const clean = s.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trimEnd()}…`;
}

function RecentCommunityPostsCard({ items, loading, error }: RecentCommunityPostsCardProps): JSX.Element {
  const top5 = items.slice(0, 5);
  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Recent community posts"
        subtitle="Last 5 from residents."
        palette="lavender"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/community">
              Open community <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="space-y-vt-2 pt-vt-5">
          {loading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load community posts"
              description="The community feed is unavailable right now."
            />
          ) : top5.length === 0 ? (
            <EmptyState
              icon={<MessagesSquare className="h-6 w-6" strokeWidth={1.75} />}
              title="No community posts yet"
              description="When residents post on the wall, the latest 5 will show here."
            />
          ) : (
            top5.map((p) => {
              const name = p.author?.full_name ?? 'Member';
              const avatar = nbhPalette(avatarPalette(name));
              const initials = name
                .split(' ')
                .map((s) => s[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              const hasReactions =
                typeof p.like_count === 'number' || typeof p.comment_count === 'number';
              return (
                <Link
                  key={p.id}
                  to="/community"
                  className="flex items-center gap-vt-3 rounded-vt-md border border-vt-border-soft bg-vt-surface p-vt-3 transition-colors duration-vt hover:bg-vt-surface-2"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-vt-13 font-bold text-white shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: avatar.fg }}
                  >
                    {initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-14 font-semibold text-vt-text">{name}</div>
                    <div className="mt-0.5 truncate text-vt-12 text-vt-text-muted">{excerpt(p.body)}</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 text-vt-12 text-vt-text-muted">
                    {hasReactions ? (
                      <div className="flex items-center gap-vt-2">
                        <span className="inline-flex items-center gap-1">
                          <Heart className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                          {p.like_count ?? 0}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                          {p.comment_count ?? 0}
                        </span>
                      </div>
                    ) : null}
                    <span>{formatRelative(p.created_at)}</span>
                  </div>
                </Link>
              );
            })
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── GuardsOnDutyCard ─────────────────────────────────────────────
// Avatar row of up to 8 active guards. The /v1/guards response does
// not carry an `on_duty` boolean today (per the server route), so we
// best-effort filter to `active !== false` and slice to 8. Each
// avatar is a mint disc with name initials; the role/shift label
// sits below; hover shows the full name as a tooltip; a small
// "+N more" pill renders when the roster exceeds the shown set.

interface GuardsOnDutyCardProps {
  items: GuardListItem[];
  loading: boolean;
  error: boolean;
}

function GuardsOnDutyCard({ items, loading, error }: GuardsOnDutyCardProps): JSX.Element {
  // Prefer explicit shift/on-duty flags when the API surfaces them; otherwise
  // fall back to "first 8 active guards" so the card always has content.
  const onShift = items.filter((g) => {
    if (g.on_duty === true || g.is_on_shift === true) return true;
    if (g.on_duty === false || g.is_on_shift === false) return false;
    return g.active !== false;
  });
  const shown = onShift.slice(0, 8);
  const extra = Math.max(0, onShift.length - shown.length);
  const mint = nbhPalette('mint');

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Guards on duty"
        subtitle="Live shift roster."
        palette="mint"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/guards">
              View all <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="pt-vt-5">
          {loading ? (
            <div className="flex flex-wrap gap-vt-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-vt-2">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-3 w-16" />
                </div>
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load guards"
              description="The guards roster is unavailable right now."
            />
          ) : shown.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.75} />}
              title="No guards configured"
              description="Add guards from the guards page to see today's roster."
            />
          ) : (
            <div className="flex flex-wrap items-start gap-vt-4">
              {shown.map((g) => {
                const initials = g.full_name
                  .split(' ')
                  .map((s) => s[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                const gateName = g.gate?.name ? `Gate ${g.gate.name}` : null;
                const towerName = g.tower?.name ? `Tower ${g.tower.name}` : null;
                const shiftLabel = g.shift ? g.shift.charAt(0).toUpperCase() + g.shift.slice(1) : null;
                const sub = gateName ?? towerName ?? shiftLabel ?? g.duty_kind ?? ' ';
                return (
                  <div
                    key={g.id}
                    className="flex w-20 flex-col items-center gap-vt-1 text-center"
                    title={g.full_name}
                  >
                    <span
                      className="flex h-12 w-12 items-center justify-center rounded-full text-vt-13 font-bold text-white shadow-vt-1 ring-2 ring-white/60"
                      style={{ backgroundColor: mint.fg }}
                    >
                      {initials}
                    </span>
                    <span className="block w-full truncate text-vt-12 font-semibold text-vt-text">
                      {g.full_name.split(' ')[0]}
                    </span>
                    <span className="block w-full truncate text-[11px] capitalize text-vt-text-muted">
                      {sub}
                    </span>
                  </div>
                );
              })}
              {extra > 0 ? (
                <div className="flex w-20 flex-col items-center gap-vt-1 text-center">
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-full text-vt-13 font-bold shadow-vt-1 ring-2 ring-white/60"
                    style={{ backgroundColor: mint.bg, color: mint.fgDeep }}
                  >
                    +{extra}
                  </span>
                  <span className="text-[11px] text-vt-text-muted">more</span>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── CctvMiniGridCard ─────────────────────────────────────────────
// 4-up grid of camera tiles. Each tile is a teal-gradient placeholder
// (no thumbnail bytes from the list endpoint — image streaming lives
// on per-snapshot routes) with a Video icon over a darker teal disc,
// the camera label in a bottom black-gradient overlay, and a small
// live-dot when the camera is online. Online inference: `is_online ??
// status === 'active'`.

interface CctvMiniGridCardProps {
  items: CameraListItem[];
  loading: boolean;
  error: boolean;
}

function CctvMiniGridCard({ items, loading, error }: CctvMiniGridCardProps): JSX.Element {
  const teal = nbhPalette('teal');
  const shown = items.slice(0, 4);

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="CCTV feed"
        subtitle="Live camera tiles."
        palette="teal"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/cameras">
              Open cameras <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="pt-vt-5">
          {loading ? (
            <div className="grid grid-cols-2 gap-vt-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="aspect-video w-full rounded-vt-md" />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
              title="Could not load cameras"
              description="The camera registry is unavailable right now."
            />
          ) : shown.length === 0 ? (
            <EmptyState
              icon={<Video className="h-6 w-6" strokeWidth={1.75} />}
              title="No cameras configured"
              description="Register CCTV cameras to see live tiles here."
            />
          ) : (
            <div className="grid grid-cols-2 gap-vt-3">
              {shown.map((c) => {
                const online = c.is_online ?? (c.status ? c.status === 'active' : true);
                return (
                  <Link
                    key={c.id}
                    to="/cameras"
                    className="group relative block aspect-video overflow-hidden rounded-vt-md ring-1 ring-vt-border-soft transition-transform duration-vt hover:scale-[1.01]"
                    style={{
                      background: `linear-gradient(135deg, ${teal.bgFrom}, ${teal.bgTo})`
                    }}
                    title={c.name}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span
                        className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-vt-2 ring-4 ring-white/40"
                        style={{ backgroundColor: teal.fg }}
                      >
                        <Video className="h-6 w-6" strokeWidth={2.5} />
                      </span>
                    </div>
                    {online ? (
                      <span className="absolute right-vt-2 top-vt-2 flex items-center gap-1 rounded-vt-pill bg-black/55 px-vt-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        <span className="vt-live-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                        Live
                      </span>
                    ) : null}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent px-vt-3 pb-vt-2 pt-vt-4">
                      <div className="truncate text-vt-12 font-semibold text-white">{c.name}</div>
                      {c.location_label ? (
                        <div className="truncate text-[11px] text-white/80">{c.location_label}</div>
                      ) : null}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── VehicleTrafficSplitCard ──────────────────────────────────────
// Three-segment donut from today's visitor list, bucketed by
// visit_kind into Resident (guest), Visitor (delivery + cab), and
// Vendor (service); the `staff` enum value defaults to "Visitor"
// since it's the closest "people coming in" bucket.
//
// Donut math: a single SVG circle per segment with a stroke-dasharray
// of `<arc-length> <C - arc-length>` and a negative stroke-dashoffset
// that rotates the segment's start to the end of the previous one.
// The whole group is rotated -90° so segment 0 starts at 12 o'clock.

interface VehicleTrafficSplitCardProps {
  visitors: Visitor[];
  loading: boolean;
}

function VehicleTrafficSplitCard({ visitors, loading }: VehicleTrafficSplitCardProps): JSX.Element {
  const teal = nbhPalette('teal');
  const sky = nbhPalette('sky');
  const lavender = nbhPalette('lavender');

  let resident = 0;
  let visitor = 0;
  let vendor = 0;
  for (const v of visitors) {
    switch (v.visit_kind) {
      case 'guest':
        resident += 1;
        break;
      case 'delivery':
      case 'cab':
        visitor += 1;
        break;
      case 'service':
        vendor += 1;
        break;
      default:
        // Unknown / staff → bucket to "Visitor" so the donut never loses a row.
        visitor += 1;
    }
  }
  const total = resident + visitor + vendor;

  const segments: Array<{ key: string; label: string; count: number; color: string }> = [
    { key: 'resident', label: 'Resident', count: resident, color: teal.fg },
    { key: 'visitor', label: 'Visitor', count: visitor, color: sky.fg },
    { key: 'vendor', label: 'Vendor', count: vendor, color: lavender.fg }
  ];

  // Donut geometry.
  const R = 40;
  const C = 2 * Math.PI * R; // ≈ 251.327
  const STROKE = 18;
  const SIZE = 120;
  const CENTER = SIZE / 2;

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Vehicle traffic split"
        subtitle="Today's entries by type."
        palette="teal"
      />
      <Card className="rounded-vt-lg">
        <CardContent className="pt-vt-5">
          {loading ? (
            <div className="flex items-center gap-vt-5">
              <Skeleton className="h-[120px] w-[120px] rounded-full" />
              <div className="flex flex-1 flex-col gap-vt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            </div>
          ) : total === 0 ? (
            <EmptyState
              icon={<Car className="h-6 w-6" strokeWidth={1.75} />}
              title="No vehicle entries today"
              description="As guards check guests in, the breakdown will appear here."
            />
          ) : (
            <div className="flex flex-col items-center gap-vt-5 sm:flex-row sm:items-center">
              <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
                <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                  {/* Track */}
                  <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={R}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth={STROKE}
                  />
                  {/* Segments — rotate -90deg so the first segment starts at 12. */}
                  <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
                    {(() => {
                      let cursor = 0;
                      return segments
                        .filter((s) => s.count > 0)
                        .map((s) => {
                          const arc = (s.count / total) * C;
                          const offset = -cursor;
                          cursor += arc;
                          return (
                            <circle
                              key={s.key}
                              cx={CENTER}
                              cy={CENTER}
                              r={R}
                              fill="none"
                              stroke={s.color}
                              strokeWidth={STROKE}
                              strokeDasharray={`${arc} ${C - arc}`}
                              strokeDashoffset={offset}
                              strokeLinecap="butt"
                            />
                          );
                        });
                    })()}
                  </g>
                </svg>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-vt-22 font-bold leading-none text-vt-text">{total}</span>
                  <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-vt-text-muted">
                    Today
                  </span>
                </div>
              </div>
              <ul className="flex flex-1 flex-col gap-vt-2">
                {segments.map((s) => {
                  const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                  return (
                    <li key={s.key} className="flex items-center gap-vt-3">
                      <span
                        aria-hidden="true"
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="flex-1 text-vt-13 font-medium text-vt-text">{s.label}</span>
                      <span className="text-vt-13 font-semibold text-vt-text">{s.count}</span>
                      <span className="w-10 text-right text-vt-12 text-vt-text-muted">{pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─── GateWiseEntriesCard ──────────────────────────────────────────
// Horizontal sky-gradient bars per gate. Today's Visitor row carries
// no gate_id (see apps/api/src/modules/visitors/routes.ts), so the
// breakdown degrades:
//   - 0 gates configured  → "no gates" empty state
//   - 1 gate              → one full-width bar with the full day's count
//   - 2+ gates            → friendly "no per-gate breakdown" empty card,
//                            since we have no way to attribute entries
// When the Visitor model gains `gate_id` we can drop the fallback and
// bucket properly.

interface GateWiseEntriesCardProps {
  visitors: Visitor[];
  gates: GateListItem[];
  loading: boolean;
}

function GateWiseEntriesCard({ visitors, gates, loading }: GateWiseEntriesCardProps): JSX.Element {
  const sky = nbhPalette('sky');
  const total = visitors.length;

  // Future-proof: if the Visitor row ever carries gate_id, bucket here.
  type V = Visitor & { gate_id?: string | null; entered_at_gate?: string | null };
  const byGate = new Map<string, number>();
  let hasGateField = false;
  for (const raw of visitors) {
    const v = raw as V;
    const gid = v.gate_id ?? v.entered_at_gate ?? null;
    if (gid) {
      hasGateField = true;
      byGate.set(gid, (byGate.get(gid) ?? 0) + 1);
    }
  }

  // Build the bar dataset.
  type Bar = { id: string; name: string; count: number };
  let bars: Bar[] = [];
  let degradedSingleGate = false;
  if (hasGateField && gates.length > 0) {
    bars = gates
      .map((g) => ({ id: g.id, name: g.name, count: byGate.get(g.id) ?? 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  } else if (gates.length === 1 && total > 0) {
    // Only one gate — safe to attribute all of today's entries to it.
    bars = [{ id: gates[0].id, name: gates[0].name, count: total }];
    degradedSingleGate = true;
  }

  const maxCount = Math.max(1, ...bars.map((b) => b.count));

  return (
    <section className="space-y-vt-3">
      <NbhSectionHeader
        title="Gate-wise entries"
        subtitle="Where today's visitors came in."
        palette="sky"
        rightAction={
          <Button asChild variant="ghost" size="sm">
            <Link to="/visitor-logs">
              View all <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        }
      />
      <Card className="rounded-vt-lg">
        <CardContent className="pt-vt-5">
          {loading ? (
            <div className="flex flex-col gap-vt-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : gates.length === 0 ? (
            <EmptyState
              icon={<DoorOpen className="h-6 w-6" strokeWidth={1.75} />}
              title="No gates configured"
              description="Add gates from the society setup to see per-gate entries."
            />
          ) : bars.length === 0 ? (
            <EmptyState
              icon={<DoorOpen className="h-6 w-6" strokeWidth={1.75} />}
              title={total === 0 ? 'No entries yet today' : 'No per-gate breakdown available'}
              description={
                total === 0
                  ? 'Entries will appear here as guards check guests in.'
                  : 'Visitor records do not yet carry a gate id — totals will appear here once that data is captured at the gate.'
              }
            />
          ) : (
            <div className="space-y-vt-3">
              {degradedSingleGate ? (
                <div className="text-vt-12 text-vt-text-muted">
                  Single gate — showing today's full entry count.
                </div>
              ) : null}
              {bars.map((b) => {
                const pct = (b.count / maxCount) * 100;
                return (
                  <div key={b.id} className="flex items-center gap-vt-3">
                    <span className="w-28 shrink-0 truncate text-vt-13 font-medium text-vt-text">
                      {b.name}
                    </span>
                    <div
                      className="relative h-5 flex-1 overflow-hidden rounded-vt-pill"
                      style={{ backgroundColor: '#eef4fa' }}
                    >
                      <div
                        className="h-full rounded-vt-pill"
                        style={{
                          width: `${Math.max(4, pct)}%`,
                          background: `linear-gradient(180deg, ${sky.bgFrom}, ${sky.fg})`
                        }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-vt-13 font-semibold text-vt-text">
                      {b.count}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
